import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    Box,
    Card,
    CardContent,
    Stack,
    Alert,
    LinearProgress,
    Button,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Divider,
    Tooltip,
    Badge,
    useTheme,
    alpha
} from '@mui/material';
import {
    GavelIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Assignment as AssignmentIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Security as SecurityIcon,
    Visibility as VisibilityIcon,
    ExpandMore as ExpandMoreIcon,
    History as HistoryIcon,
    Assessment as AssessmentIcon,
    Rule as RuleIcon,
    Done as DoneIcon,
    DoneAll as DoneAllIcon,
    PendingActions as PendingActionsIcon,
    Verified as VerifiedIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

export default function ReviewStep({ 
    formData, 
    setFormData, 
    onValidationChange,
    onApproval,
    showSnackbar,
    currentUser,
    batchTimeFormat = '12hour'
}) {
    const theme = useTheme();
    const [validationErrors, setValidationErrors] = useState({});
    const [reviewComplete, setReviewComplete] = useState(false);
    const [complianceChecks, setComplianceChecks] = useState({});
    const [approvalWorkflow, setApprovalWorkflow] = useState([]);
    const [showSignatureDialog, setShowSignatureDialog] = useState(false);
    const [activeApprovalStep, setActiveApprovalStep] = useState(0);
    const [reviewSummary, setReviewSummary] = useState({});
    const [currentStepId, setCurrentStepId] = useState('');
    const [signaturePreviewName, setSignaturePreviewName] = useState('');

    // Initialize approval workflow based on test criticality
    useEffect(() => {
        if (formData.is_critical_test) {
            setApprovalWorkflow([
                {
                    id: 'supervisor_review',
                    title: 'Supervisor Review',
                    role: 'qa_supervisor',
                    description: 'Supervisory review and approval for critical tests',
                    required: true,
                    completed: false
                }
            ]);
        } else {
            setApprovalWorkflow([
                {
                    id: 'qa_manager_approval',
                    title: 'QA Manager Approval',
                    role: 'qa_manager',
                    description: 'QA manager review and approval for standard tests',
                    required: true,
                    completed: false
                }
            ]);
        }
    }, [formData.is_critical_test]);

    // Initialize signature preview when dialog opens
    useEffect(() => {
        if (showSignatureDialog) {
            setSignaturePreviewName('');
        }
    }, [showSignatureDialog]);



    // All tests are now pass/fail only
    const getSpecificationType = () => 'pass_fail';

    // Comprehensive validation logic
    const validateReview = () => {
        const errors = {};
        const specificationType = getSpecificationType(formData.test_type);
        
        // Check if all previous steps are complete based on test type
        if (specificationType === 'pass_fail') {
            if (!formData.result_text) {
                errors.incomplete_results = 'Test results must be recorded before review';
            }
        } else {
            if (!formData.actual_result) {
                errors.incomplete_results = 'Test results must be recorded before review';
            }
        }
        
        if (!formData.tested_by_name) {
            errors.incomplete_sampling = 'Test execution information is incomplete';
        }
        
        if (!formData.test_type || !formData.test_method) {
            errors.incomplete_config = 'Test configuration is incomplete';
        }

        // Compliance checks
        const compliance = performComplianceChecks();
        setComplianceChecks(compliance);
        
        if (compliance.critical_failures > 0) {
            errors.compliance_failed = `${compliance.critical_failures} critical compliance check(s) failed`;
        }

        // Approval workflow validation
        const completedApprovals = approvalWorkflow.filter(step => step.completed);
        const requiredApprovals = approvalWorkflow.filter(step => step.required);
        
        if (completedApprovals.length < requiredApprovals.length) {
            errors.incomplete_approvals = `${requiredApprovals.length - completedApprovals.length} approval(s) still pending`;
        }

        setValidationErrors(errors);
        const isValid = Object.keys(errors).length === 0;
        setReviewComplete(isValid);
        
        return isValid;
    };

    // Perform comprehensive compliance checks
    const performComplianceChecks = () => {
        const checks = {
            critical_failures: 0,
            warnings: 0,
            passed: 0,
            details: []
        };

        const specificationType = getSpecificationType(formData.test_type);

        // Check 1: Data integrity - pass/fail only
        if (formData.result_text && (formData.result_text === 'PASS' || formData.result_text === 'FAIL')) {
            checks.passed++;
            checks.details.push({
                category: 'Data Integrity',
                check: 'Complete test data',
                status: 'passed',
                description: 'Pass/fail result is properly recorded'
            });
        } else {
            checks.critical_failures++;
            checks.details.push({
                category: 'Data Integrity',
                check: 'Complete test data',
                status: 'failed',
                description: 'Missing pass/fail result'
            });
        }

        // Check 2: Specification compliance - pass/fail only
        if (formData.result_text === 'PASS') {
            checks.passed++;
            checks.details.push({
                category: 'Specification Compliance',
                check: 'Test result compliance',
                status: 'passed',
                description: 'Test passed acceptance criteria'
            });
        } else if (formData.result_text === 'FAIL') {
            checks.warnings++;
            checks.details.push({
                category: 'Specification Compliance',
                check: 'Test result compliance',
                status: 'warning',
                description: 'Test failed acceptance criteria - documented for investigation'
            });
        }

        // Check 3: Test execution documentation
        if (formData.tested_by_name && formData.test_datetime && formData.sample_location) {
            checks.passed++;
            checks.details.push({
                category: 'Documentation',
                check: 'Test execution records',
                status: 'passed',
                description: 'Complete test execution documentation'
            });
        } else {
            checks.warnings++;
            checks.details.push({
                category: 'Documentation',
                check: 'Test execution records',
                status: 'warning',
                description: 'Incomplete test execution documentation'
            });
        }

        // Check 4: Test method compliance
        if (formData.test_type && formData.test_method && formData.test_standard) {
            checks.passed++;
            checks.details.push({
                category: 'Method Compliance',
                check: 'Test method documentation',
                status: 'passed',
                description: 'Test method properly documented'
            });
        } else {
            checks.critical_failures++;
            checks.details.push({
                category: 'Method Compliance',
                check: 'Test method documentation',
                status: 'failed',
                description: 'Incomplete test method documentation'
            });
        }

        // Check 5: Critical test requirements
        if (formData.is_critical_test) {
            if (formData.qa_review_required) {
                checks.passed++;
                checks.details.push({
                    category: 'Critical Test',
                    check: 'QA review requirement',
                    status: 'passed',
                    description: 'QA review properly flagged for critical test'
                });
            } else {
                checks.warnings++;
                checks.details.push({
                    category: 'Critical Test',
                    check: 'QA review requirement',
                    status: 'warning',
                    description: 'QA review should be required for critical tests'
                });
            }
        }

        // Check 6: Date/time validation
        if (formData.test_datetime) {
            const testDate = dayjs(formData.test_datetime);
            const now = dayjs();
            
            if (testDate.isBefore(now.add(1, 'hour'))) {
                checks.passed++;
                checks.details.push({
                    category: 'Temporal Validation',
                    check: 'Test date/time validity',
                    status: 'passed',
                    description: 'Test date/time is valid'
                });
            } else {
                checks.critical_failures++;
                checks.details.push({
                    category: 'Temporal Validation',
                    check: 'Test date/time validity',
                    status: 'failed',
                    description: 'Test date/time appears to be in the future'
                });
            }
        }

        return checks;
    };

    // Generate review summary
    const generateReviewSummary = () => {
        const actualResult = parseFloat(formData.actual_result);
        const minAcceptable = parseFloat(formData.min_acceptable);
        const maxAcceptable = parseFloat(formData.max_acceptable);
        
        const summary = {
            test_details: {
                test_type: formData.test_type,
                test_method: formData.test_method,
                test_standard: formData.test_standard,
                sample_location: formData.sample_location
            },
            results: {
                actual_result: formData.actual_result,
                target_value: formData.target_value,
                min_acceptable: formData.min_acceptable,
                max_acceptable: formData.max_acceptable,
                unit: formData.target_unit,
                result_text: formData.result_text,
                within_spec: !isNaN(actualResult) && !isNaN(minAcceptable) && !isNaN(maxAcceptable) 
                    ? actualResult >= minAcceptable && actualResult <= maxAcceptable 
                    : false
            },
            execution: {
                tested_by: formData.tested_by_name,
                test_date: formData.test_datetime,
                notes: formData.notes
            },
            attachments: {
                photos: formData.photo_urls?.length || 0,
                documents: formData.document_urls?.length || 0
            }
        };
        
        setReviewSummary(summary);
    };

    // Notify parent of validation status when data changes
    useEffect(() => {
        const timer = setTimeout(() => {
            const isValid = validateReview();
            generateReviewSummary();
            if (onValidationChange) {
                onValidationChange('review', isValid);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [formData, approvalWorkflow]);

    const handleApprovalStepComplete = (stepId, approverData) => {
        setApprovalWorkflow(prev => prev.map(step => 
            step.id === stepId 
                ? { 
                    ...step, 
                    completed: true, 
                    approver: approverData.name,
                    approval_date: dayjs().toISOString(),
                    signature: approverData.signature,
                    comments: approverData.comments
                }
                : step
        ));
        
        const nextStep = approvalWorkflow.findIndex(step => step.id === stepId) + 1;
        if (nextStep < approvalWorkflow.length) {
            setActiveApprovalStep(nextStep);
        }
        
        showSnackbar?.(`Approval step "${stepId}" completed`, 'success');
    };

    const handleSignatureSubmit = () => {
        const signatureName = document.getElementById('approval-signature-name')?.value || '';
        const signatureRole = document.getElementById('approval-signature-role')?.value || '';
        const signatureComments = document.getElementById('approval-signature-comments')?.value || '';
        
        if (!signatureName.trim()) {
            showSnackbar?.('Name is required for electronic signature', 'error');
            return;
        }
        
        if (!signatureRole.trim()) {
            showSnackbar?.('Role/Title is required for electronic signature', 'error');
            return;
        }
        
        const approverData = {
            name: signatureName,
            signature: signatureName,
            role: signatureRole,
            comments: signatureComments
        };
        
        // Update formData with signature information
        if (setFormData) {
            setFormData(prev => {
                const updatedFormData = {
                    ...prev,
                    qa_signature: signatureName,  // Store the actual signature text
                    qa_signature_name: signatureName,
                    qa_signature_role: signatureRole,
                    qa_signature_date: dayjs(),
                    qa_review_comments: signatureComments,
                    qa_reviewed_by_name: signatureName,
                    qa_reviewed_by_role: signatureRole,
                    qa_review_datetime: dayjs()
                };
                return updatedFormData;
            });
        }
        
        handleApprovalStepComplete(currentStepId, approverData);
        setShowSignatureDialog(false);
        setSignaturePreviewName('');
    };

    const openSignatureDialog = (stepId) => {
        setCurrentStepId(stepId);
        setShowSignatureDialog(true);
    };

    const handleFinalApproval = () => {
        const allApprovalsComplete = approvalWorkflow.every(step => step.completed || !step.required);
        
        if (reviewComplete && allApprovalsComplete) {
            // Include the complete updated formData with all signature fields
            const approvalData = {
                test_record: { ...formData }, // Include ALL formData fields
                compliance_checks: complianceChecks,
                approval_workflow: approvalWorkflow,
                final_approval_date: dayjs().toISOString(),
                approved_by: currentUser?.name || 'System',
                // Pass signature data in the expected format
                signature: formData.qa_signature || formData.qa_signature_name,
                qa_signature: formData.qa_signature || formData.qa_signature_name,
                qa_signature_name: formData.qa_signature_name,
                qa_signature_role: formData.qa_signature_role,
                reviewerName: formData.qa_signature_name,
                reviewerRole: formData.qa_signature_role,
                comments: formData.qa_review_comments,
                qa_review_comments: formData.qa_review_comments
            };
            
            if (onApproval) {
                onApproval(approvalData);
            } else {
                showSnackbar?.('Production test marked as approved', 'success');
            }
        } else {
            showSnackbar?.('Cannot approve - please complete all required steps', 'error');
        }
    };

    const getComplianceStatusColor = (status) => {
        switch (status) {
            case 'passed': return theme.palette.success.main;
            case 'warning': return theme.palette.warning.main;
            case 'failed': return theme.palette.error.main;
            default: return theme.palette.grey[500];
        }
    };

    const getComplianceStatusIcon = (status) => {
        switch (status) {
            case 'passed': return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
            case 'warning': return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
            case 'failed': return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
            default: return <PendingActionsIcon sx={{ color: theme.palette.grey[500] }} />;
        }
    };

    // Get review completion percentage
    const getCompletionPercentage = () => {
        let total = 0;
        let completed = 0;
        
        // Compliance checks weight
        if (Object.keys(complianceChecks).length > 0) {
            total += 40;
            if (complianceChecks.critical_failures === 0) completed += 40;
        }
        
        // Approval workflow weight
        if (approvalWorkflow.length > 0) {
            total += 60;
            const completedApprovals = approvalWorkflow.filter(step => step.completed).length;
            completed += (completedApprovals / approvalWorkflow.length) * 60;
        }
        
        return total > 0 ? (completed / total) * 100 : 0;
    };

    const completionPercentage = getCompletionPercentage();

    return (
        <Box sx={{ p: 4 }}>
            {/* Header with Progress */}
            <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mb: 4
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)',
                            mr: 1
                        }}>
                            <VerifiedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        </Box>
                        <Box flex={1}>
                            <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                                Review & Approval
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Compliance validation, approval workflow, and final sign-off
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h5" sx={{ fontWeight: 500, color: reviewComplete ? theme.palette.success.main : theme.palette.warning.main }}>
                                {Math.round(completionPercentage)}%
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Complete
                            </Typography>
                        </Box>
                    </Stack>
                    
                    <LinearProgress 
                        variant="determinate" 
                        value={completionPercentage} 
                        sx={{ 
                            height: 8, 
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.grey[400], 0.3),
                            '& .MuiLinearProgress-bar': {
                                background: reviewComplete 
                                    ? `linear-gradient(90deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`
                                    : `linear-gradient(90deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`
                            }
                        }} 
                    />
                </CardContent>
            </Card>
            
            <Grid container spacing={4}>

            {/* Validation Alerts */}
            {Object.keys(validationErrors).length > 0 && (
                <Grid item xs={12}>
                    <Alert 
                        severity="error"
                        sx={{ 
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: theme.palette.error.main
                            }
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: theme.palette.error.main }}>
                            Review Issues Found:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {Object.values(validationErrors).map((error, index) => (
                                <li key={index}>
                                    <Typography variant="body2" sx={{ color: theme.palette.error.main, fontWeight: 500 }}>{error}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Alert>
                </Grid>
            )}

            {/* Test Record Summary */}
            <Grid item xs={12}>
                <Accordion defaultExpanded sx={{
                    borderRadius: '24px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    overflow: 'hidden',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&.MuiAccordion-root': {
                        borderRadius: '24px !important',
                        '&:before': {
                            display: 'none'
                        },
                        '&:first-of-type': {
                            borderRadius: '24px !important'
                        },
                        '&:last-of-type': {
                            borderRadius: '24px !important'
                        }
                    },
                    '& .MuiAccordion-region': {
                        borderRadius: '0 0 24px 24px !important'
                    },
                    '& .MuiCollapse-root': {
                        borderRadius: '0 0 24px 24px !important'
                    }
                }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{
                        borderRadius: '24px 24px 0 0',
                        backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    }}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Test Record Summary
                            </Typography>
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 3, 
                                    borderRadius: 4,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    background: 'white',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: theme.palette.primary.main }}>
                                        Test Configuration
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Type:</strong> {reviewSummary.test_details?.test_type || 'Not specified'}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Method:</strong> {reviewSummary.test_details?.test_method || 'Not specified'}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Standard:</strong> {reviewSummary.test_details?.test_standard || 'Not specified'}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Sample Location:</strong> {reviewSummary.test_details?.sample_location || 'Not specified'}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 3, 
                                    borderRadius: 4,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    background: 'white',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: theme.palette.primary.main }}>
                                        Results
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        <strong>Result:</strong> {reviewSummary.results?.result_text || 'Not recorded'}
                                    </Typography>
                                    <Box sx={{ height: 60 }} />
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 3, 
                                    borderRadius: 4,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    background: 'white',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: theme.palette.primary.main }}>
                                        Execution Details
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Tested By:</strong> {reviewSummary.execution?.tested_by || 'Not specified'}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        <strong>Test Date:</strong> {reviewSummary.execution?.test_date ? dayjs(reviewSummary.execution.test_date).format(batchTimeFormat === '24hour' ? 'MMM D, YYYY HH:mm' : 'MMM D, YYYY h:mm A') : 'Not specified'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Notes:</strong> {reviewSummary.execution?.notes || 'None'}</Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ 
                                    p: 3, 
                                    borderRadius: 4,
                                    border: `1px solid ${theme.palette.grey[300]}`,
                                    background: 'white',
                                    backdropFilter: 'blur(10px)',
                                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                                    }
                                }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: theme.palette.primary.main }}>
                                        Documentation
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Photos:</strong> {reviewSummary.attachments?.photos || 0} files</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Documents:</strong> {reviewSummary.attachments?.documents || 0} files</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}><strong>Critical Test:</strong> {formData.is_critical_test ? 'Yes' : 'No'}</Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Grid>

            {/* Compliance Checks */}
            <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 4,
                    border: `1px solid ${complianceChecks.critical_failures > 0 ? theme.palette.error.main : theme.palette.success.main}`,
                    background: 'white',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                            <RuleIcon sx={{ color: complianceChecks.critical_failures > 0 ? theme.palette.error.main : theme.palette.success.main }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                    Compliance Validation
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    Automated compliance checks against regulatory requirements
                                </Typography>
                            </Box>
                            <Box sx={{ ml: 'auto' }}>
                                <Stack direction="row" spacing={2}>
                                    <Chip 
                                        label={`${complianceChecks.passed || 0} Passed`}
                                        color="success"
                                        size="small"
                                    />
                                    <Chip 
                                        label={`${complianceChecks.warnings || 0} Warnings`}
                                        color="warning"
                                        size="small"
                                    />
                                    <Chip 
                                        label={`${complianceChecks.critical_failures || 0} Failed`}
                                        color="error"
                                        size="small"
                                    />
                                </Stack>
                            </Box>
                        </Stack>

                        {complianceChecks.details && (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Category</TableCell>
                                            <TableCell>Check</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Description</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {complianceChecks.details.map((check, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{check.category}</TableCell>
                                                <TableCell>{check.check}</TableCell>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        {getComplianceStatusIcon(check.status)}
                                                        <Typography variant="body2" sx={{ color: getComplianceStatusColor(check.status), fontWeight: 500 }}>
                                                            {check.status.toUpperCase()}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {check.description}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* Approval Workflow */}
            <Grid item xs={12}>
                <Card sx={{ 
                    borderRadius: 4,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                            <SecurityIcon sx={{ color: theme.palette.primary.main }} />
                            <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                Approval Workflow
                            </Typography>
                        </Stack>

                        <Stepper orientation="vertical" activeStep={activeApprovalStep}>
                            {approvalWorkflow.map((step, index) => (
                                <Step key={step.id} completed={step.completed}>
                                    <StepLabel 
                                        icon={step.completed ? <DoneAllIcon /> : <PendingActionsIcon />}
                                        sx={{
                                            '& .MuiStepLabel-label': {
                                                fontWeight: step.completed ? 500 : 500,
                                                color: step.completed ? theme.palette.success.main : 'inherit'
                                            }
                                        }}
                                    >
                                        {step.title}
                                        {step.required && <Chip label="Required" size="small" color="primary" sx={{ ml: 1 }} />}
                                    </StepLabel>
                                    <StepContent>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {step.description}
                                        </Typography>
                                        
                                        {step.completed ? (
                                            <Paper sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
                                                <Typography variant="body2"><strong>Approved by:</strong> {step.approver}</Typography>
                                                <Typography variant="body2">
                                                    <strong>Date:</strong> {dayjs(step.approval_date).format(batchTimeFormat === '24hour' ? 'MMM D, YYYY HH:mm' : 'MMM D, YYYY h:mm A')}
                                                </Typography>
                                                {step.comments && (
                                                    <Typography variant="body2"><strong>Comments:</strong> {step.comments}</Typography>
                                                )}
                                            </Paper>
                                        ) : (
                                            <Box sx={{ mb: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => openSignatureDialog(step.id)}
                                                    disabled={complianceChecks.critical_failures > 0}
                                                    sx={{
                                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                                                        color: 'white',
                                                        fontWeight: 500,
                                                        borderRadius: 3,
                                                        border: 'none',
                                                        backdropFilter: 'blur(10px)',
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                                                        overflow: 'hidden',
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                                                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.7)})`,
                                                            border: 'none'
                                                        }
                                                    }}
                                                >
                                                    Complete Approval
                                                </Button>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                    Role required: {step.role}
                                                </Typography>
                                            </Box>
                                        )}
                                    </StepContent>
                                </Step>
                            ))}
                        </Stepper>
                    </CardContent>
                </Card>
            </Grid>

            {/* Final Approval */}
            {reviewComplete && (
                <Grid item xs={12}>
                    <Alert 
                        severity="success"
                        sx={{ 
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            '& .MuiAlert-icon': {
                                color: theme.palette.success.main
                            }
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <CheckCircleIcon />
                            <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                                ✓ Ready for Final Approval
                            </Typography>
                        </Stack>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                onClick={handleFinalApproval}
                                startIcon={<VerifiedIcon />}
                                sx={{ 
                                    fontWeight: 500,
                                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${alpha(theme.palette.success.main, 0.8)})`,
                                    borderRadius: 3,
                                    border: 'none',
                                    boxShadow: 'none',
                                    overflow: 'hidden',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: 'none',
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.9)}, ${alpha(theme.palette.success.main, 0.7)})`,
                                        border: 'none'
                                    }
                                }}
                            >
                                Finalize & Approve Test Record
                            </Button>
                        </Box>
                    </Alert>
                </Grid>
            )}

            {/* Electronic Signature Dialog */}
            <Dialog 
                open={showSignatureDialog} 
                onClose={() => setShowSignatureDialog(false)} 
                maxWidth="sm" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 4,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: '1.25rem'
                }}>
                    Electronic Signature Required
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mt: 2 }}>
                                Complete approval step with electronic signature:
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                id="approval-signature-name"
                                label="Full Name *"
                                fullWidth
                                autoFocus
                                required
                                placeholder="Enter your full name"
                                onChange={(e) => setSignaturePreviewName(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { 
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            borderRadius: 3
                                        },
                                        '&:hover fieldset': { 
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '&.Mui-focused fieldset': { 
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '& input': {
                                            py: 2,
                                            fontWeight: 500
                                        },
                                        backgroundColor: 'white',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                        }
                                    }
                                }}
                            />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                id="approval-signature-role"
                                label="Role/Title *"
                                fullWidth
                                required
                                placeholder="Enter your role or title"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { 
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            borderRadius: 3
                                        },
                                        '&:hover fieldset': { 
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '&.Mui-focused fieldset': { 
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '& input': {
                                            py: 2,
                                            fontWeight: 500
                                        },
                                        backgroundColor: 'white',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                id="approval-signature-comments"
                                fullWidth
                                multiline
                                rows={3}
                                label="Comments (Optional)"
                                placeholder="Add any review comments..."
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { 
                                            border: `1px solid ${theme.palette.grey[400]}`,
                                            borderRadius: 3
                                        },
                                        '&:hover fieldset': { 
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '&.Mui-focused fieldset': { 
                                            border: `1px solid ${theme.palette.primary.main}`
                                        },
                                        '& textarea': {
                                            py: 2,
                                            fontWeight: 500
                                        },
                                        backgroundColor: 'white',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        backdropFilter: 'blur(10px)',
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ 
                                mt: 2, 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 3, 
                                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                backdropFilter: 'blur(10px)'
                            }}>
                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500 }}>
                                    Preview: Your signature will appear in script font
                                </Typography>
                                <Box sx={{ 
                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                    fontSize: '28px', 
                                    color: '#000000',
                                    mt: 2,
                                    mb: 1,
                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                    minHeight: '40px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {signaturePreviewName || 'Preview will appear here'}
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary" sx={{ 
                                fontStyle: 'italic',
                                p: 2,
                                backgroundColor: '#fff3e0',
                                borderRadius: 1,
                                border: '1px solid #ffb74d'
                            }}>
                                By providing your electronic signature, you certify that you have reviewed this production test record 
                                and approve it in accordance with company procedures and applicable regulations.
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                    <Button 
                        onClick={() => setShowSignatureDialog(false)}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1,
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[400], 0.1),
                                transform: 'translateY(-1px)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="contained" 
                        onClick={handleSignatureSubmit}
                        sx={{
                            borderRadius: 3,
                            px: 4,
                            py: 1,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.8)})`,
                            color: 'white',
                            fontWeight: 500,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            backdropFilter: 'blur(10px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.7)})`
                            }
                        }}
                    >
                        Sign & Approve
                    </Button>
                </DialogActions>
            </Dialog>
            </Grid>
        </Box>
    );
} 